import { Popover } from '@headlessui/react';
import { Info } from 'lucide-react';
import { useState } from 'react';

interface InfoTooltipProps {
    text: string;
    iconSize?: number;
    position?: 'top' | 'bottom' | 'right' | 'left';
    /**
     * Para `top` y `bottom`: `center` cuelga el globo del centro del icono y
     * `start` lo alinea con su borde izquierdo. Hace falta porque el globo es un
     * `div` absoluto sin portal: centrado junto al borde de un contenedor con
     * `overflow` se corta, y el cuerpo del detalle de evento es `overflow-y-auto`
     * (CSS convierte también el eje x en `auto`, así que recorta por los lados).
     */
    align?: 'center' | 'start';
    iconClassName?: string;
}

const InfoTooltip = ({
    text,
    iconSize = 16,
    position = 'bottom',
    align = 'center',
    iconClassName = ''
}: InfoTooltipProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    
    // Definir estilos según la posición
    const getPositionStyles = () => {
        switch (position) {
            case 'right':
                return {
                    tooltip: "left-full ml-2 top-1/2 -translate-y-1/2",
                    arrow: "-left-1 top-1/2 -translate-y-1/2 rotate-[-45deg]"
                };
            case 'left':
                return {
                    tooltip: "right-full mr-2 top-1/2 -translate-y-1/2",
                    arrow: "-right-1 top-1/2 -translate-y-1/2 rotate-[135deg]"
                };
            case 'top':
                return {
                    tooltip: align === 'start'
                        ? "bottom-full mb-2 left-0"
                        : "bottom-full mb-2 -translate-x-1/2 left-1/2",
                    arrow: align === 'start'
                        ? "-bottom-1 left-2 rotate-[135deg]"
                        : "-bottom-1 left-1/2 -translate-x-1/2 rotate-[135deg]"
                };
            case 'bottom':
            default:
                return {
                    tooltip: align === 'start'
                        ? "top-full mt-2 left-0"
                        : "top-full mt-2 -translate-x-1/2 left-1/2",
                    arrow: align === 'start'
                        ? "-top-1 left-2 rotate-45"
                        : "-top-1 left-1/2 -translate-x-1/2 rotate-45"
                };
        }
    };
    
    const positionStyles = getPositionStyles();
    
    return (
        <Popover className="relative inline-flex items-center">
            <Info 
                size={iconSize} 
                className={iconClassName}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onClick={() => setShowTooltip(!showTooltip)}
                aria-label="Más información"
            />
            {showTooltip && (
                <div className={`absolute z-10 w-64 px-4 py-2 text-sm text-white bg-gray-800 rounded-md shadow-lg ${positionStyles.tooltip}`}>
                    <div className="whitespace-pre-line">{text}</div>
                    <div className={`absolute w-2 h-2 bg-gray-800 ${positionStyles.arrow}`}></div>
                </div>
            )}
        </Popover>
    );
};

export default InfoTooltip;